import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Package, 
  ShoppingBag, 
  Box, 
  Tag,
  CheckCircle,
  ArrowRight,
  Factory,
  Lightbulb,
  Award,
  Clock,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import BoxAnimation from '../components/BoxAnimation';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const heroRef = useRef(null);
  const sectionsRef = useRef([]);

  useEffect(() => {
    // Animate sections on scroll
    sectionsRef.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(section, 
          {
            y: 60,
            opacity: 0
          },
          {
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
              toggleActions: 'play none none none'
            },
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power3.out'
          }
        );
      }
    });
  }, []);

  const services = [
    {
      icon: Package,
      title: 'Custom Boxes',
      description: 'Tailored packaging solutions designed to your exact specifications and brand requirements.'
    },
    {
      icon: Box,
      title: 'Corrugated Packaging',
      description: 'Industrial-strength corrugated solutions for secure transportation and storage.'
    },
    {
      icon: ShoppingBag,
      title: 'E-commerce Mailers',
      description: 'Durable, branded mailer boxes optimized for shipping and unboxing experiences.'
    },
    {
      icon: Tag,
      title: 'Labels & Tags',
      description: 'Professional labels and tags that enhance product presentation and brand identity.'
    }
  ];

  const strengths = [
    {
      icon: Award,
      title: '30+ Years Experience',
      description: 'Three decades of industry expertise delivering consistent quality'
    },
    {
      icon: Lightbulb,
      title: 'Design Excellence',
      description: 'In-house design team providing expert guidance from concept to completion'
    },
    {
      icon: Factory,
      title: 'Manufacturing Capability',
      description: 'Modern infrastructure with advanced machinery and quality materials'
    },
    {
      icon: CheckCircle,
      title: 'Quality Assurance',
      description: 'Rigorous quality control at every stage of production'
    },
    {
      icon: Clock,
      title: 'Reliable Delivery',
      description: 'Consistent on-time delivery with secure handling and logistics'
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Consultation',
      description: 'Understanding your product requirements, branding needs, and functional expectations'
    },
    {
      number: '02',
      title: 'Design',
      description: 'Developing packaging concepts tailored to your product and brand identity'
    },
    {
      number: '03',
      title: 'Prototyping',
      description: 'Creating physical samples to evaluate structure, durability, and aesthetics'
    },
    {
      number: '04',
      title: 'Manufacturing',
      description: 'Precision production using advanced machinery and quality materials'
    },
    {
      number: '05',
      title: 'Delivery',
      description: 'Ensuring timely and secure dispatch of finished packaging'
    }
  ];

  const industries = [
    'E-commerce',
    'Retail',
    'Food & Beverage',
    'Cosmetics & Beauty',
    'Logistics',
    'Manufacturing'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-50" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <h1 className="text-6xl lg:text-7xl font-bold text-navy-900 leading-tight">
                End-to-End
                <br />
                <span className="text-gray-600">Packaging Solutions</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                From concept to creation, we design and manufacture packaging that reflects your brand and protects your product.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  size="lg" 
                  className="bg-navy-900 hover:bg-navy-800 text-white text-lg px-8 py-6"
                  asChild
                >
                  <Link to="/portfolio">
                    Explore Our Work <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white text-lg px-8 py-6"
                  asChild
                >
                  <Link to="/solutions">View Solutions</Link>
                </Button>
              </div>
            </div>

            {/* Right - 3D Box Animation */}
            <div className="h-96 lg:h-[600px] relative flex items-center justify-center">
              <BoxAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* Brand Introduction */}
      <section ref={el => sectionsRef.current[0] = el} className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold text-navy-900">
              Your Packaging Partner
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Sagar Pack delivers comprehensive packaging solutions for businesses of all sizes. 
              Whether you're a startup launching your first product or an established enterprise 
              scaling production, we provide the expertise, infrastructure, and support to get 
              your packaging right.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section ref={el => sectionsRef.current[1] = el} className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Packaging Solutions
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive range of packaging products and services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6 space-y-4 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-900 rounded-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-navy-900">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white"
              asChild
            >
              <Link to="/solutions">
                View All Solutions <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Sagar Pack */}
      <section ref={el => sectionsRef.current[2] = el} className="py-20 bg-navy-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose Sagar Pack
            </h2>
            <p className="text-lg text-gray-300">
              Proven expertise, reliable execution, and long-term partnership
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strengths.map((strength, index) => {
              const IconComponent = strength.icon;
              return (
                <div key={index} className="space-y-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-lg">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">{strength.title}</h3>
                  <p className="text-gray-300">{strength.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section ref={el => sectionsRef.current[3] = el} className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Our Process
            </h2>
            <p className="text-lg text-gray-600">
              A structured approach from concept to delivery
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {process.map((step, index) => (
              <div 
                key={index}
                className="flex items-start space-x-6 p-6 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-lg bg-navy-900 text-white flex items-center justify-center text-xl font-bold">
                    {step.number}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-navy-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white"
              asChild
            >
              <Link to="/process">
                Learn More About Our Process <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section ref={el => sectionsRef.current[4] = el} className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Our Work
            </h2>
            <p className="text-lg text-gray-600">
              Explore packaging solutions delivered across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'https://images.unsplash.com/photo-1716193348749-0be58bc06ad8',
              'https://images.unsplash.com/photo-1626253934161-08cfea22e968',
              'https://images.unsplash.com/photo-1716540103530-cc33cdd20cde',
              'https://images.unsplash.com/photo-1755606277170-4e89d5847e14'
            ].map((image, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 group h-80"
              >
                <img 
                  src={image}
                  alt={`Project ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent opacity-60"></div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="bg-navy-900 hover:bg-navy-800 text-white"
              asChild
            >
              <Link to="/portfolio">
                View Full Portfolio <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section ref={el => sectionsRef.current[5] = el} className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600">
              Packaging expertise across diverse business sectors
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="px-8 py-4 bg-gray-50 border-2 border-gray-200 rounded-lg hover:border-navy-900 hover:shadow-lg transition-all duration-300"
              >
                <span className="text-lg font-medium text-navy-900">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section ref={el => sectionsRef.current[6] = el} className="py-20 bg-navy-900 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Discuss Your Packaging Needs?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get in touch to explore how we can support your packaging requirements
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-navy-900 hover:bg-gray-100 text-lg px-8 py-6"
              asChild
            >
              <Link to="/contact">
                Contact Us <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-navy-900 text-lg px-8 py-6"
              asChild
            >
              <a href="tel:+918879049450">
                Call +91 88790 49450
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
